
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { motion } from "framer-motion";

export const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, isLoading } = useAuth();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    try {
      console.log("Attempting login with:", email);
      await signIn(email, password);
    } catch (error: any) {
      console.error("Error signing in:", error);
      
      // Provide more helpful error messages
      if (error.message?.toLowerCase().includes("invalid login credentials")) {
        setError("The email or password you entered is incorrect");
      } else {
        setError(error.message || "An error occurred during login");
      }
    }
  };

  return (
    <motion.form 
      onSubmit={handleLogin} 
      className="space-y-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {error && (
        <Alert variant="destructive" className="animate-fade-in">
          <AlertCircle className="h-4 w-4 mr-2" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div>
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-background/80 backdrop-blur-sm hover:bg-background/90 transition-all duration-300"
        />
      </div>
      
      <div className="relative">
        <Input
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="bg-background/80 backdrop-blur-sm hover:bg-background/90 transition-all duration-300 pr-10"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-0 top-0 h-full px-3 transition-colors"
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Eye className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="sr-only">
            {showPassword ? "Hide password" : "Show password"}
          </span>
        </Button>
      </div>
      
      <div className="flex justify-between items-center">
        <Link
          to="/auth/forgot-password"
          className="text-sm text-accent hover:text-accent/80 transition-colors"
        >
          Forgot password?
        </Link>
      </div>
      
      <Button 
        type="submit" 
        className="w-full shadow-neo transition-all duration-300 hover:shadow-neo-lg" 
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
            <span>Signing in...</span>
          </>
        ) : (
          "Sign In"
        )}
      </Button>
    </motion.form>
  );
};
